import path from 'path';
import { readFile } from 'fs/promises';
import Debug from '../../helpers/debug.js';
import ora from 'ora';
import AWS from 'aws-sdk';
import Table from 'cli-table';
import { getFolderFiles } from '../../helpers/utils.js';
import descriptions from '../../helpers/descriptions.js';
import { pr, cl, themed, printJSON } from '../../helpers/print.js';
import {
  genCredentialsOnClientApi,
  getLinkedConnectors,
  getLinkedSources,
  getSupportedConnectors,
  getSupportedFlows,
  getSupportedSources,
} from '../../helpers/api.js';
import { isV2Flow, getEntityLabel } from '../../helpers/flow.js';

const debug = Debug('commands:etl:deploy');

const command = 'deploy';

const desc = 'Deploy ETL scripts';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('flow', descriptions.options['flow'].config)
    .demandOption('flow', descriptions.options['flow'].demandText)
    .option('tap', descriptions.options['tap'].config)
    .option('all', descriptions.options['all'].config)
    .option('tenant', { ...descriptions.options['tenant'].config, default: 'default' })
    .option('sourceFolder', descriptions.options['sourceFolder'].config)
    .demandOption('sourceFolder', descriptions.options['sourceFolder'].demandText);
};

const validateEtlScriptFiles = (filenames) => {

      const ETL_SCRIPT_BLACKLISTED_FILES = [
        "config.json",
        "source-config.json",
        "target-config.json",
        "catalog.json",
        "target-catalog.json",
        "state.json"
    ]

    const ETL_SCRIPT_BLACKLISTED_DIRECTORIES = [
        "sync-output",
        "snapshots",
        "etl-output",
        ".venv"
    ]
    for (const filename of filenames) {
        if (ETL_SCRIPT_BLACKLISTED_FILES.includes(filename)) {
            throw new Error(`File ${filename} is a reserved file name and cannot be used in the ETL script.`);
        }
        if (ETL_SCRIPT_BLACKLISTED_DIRECTORIES.some((directory) => filename.includes(directory))) {
            throw new Error(`Directory ${directory} is a reserved directory name and cannot be used in the ETL script.`);
        }
    }
}

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, flow, tap, all, tenant, sourceFolder } = argv;
  const { clientApiBaseUri } = hg;

  if (!all && !tap) {
    throw new Error(
      `${descriptions.options['tap'].demandText} Or you can pass the --all flag to run this command for all taps/connectors.`
    );
  }

  let message;
  let spinner = ora();
  const folderPrefix = path.resolve(process.cwd(), sourceFolder);
  try {
    message = themed(
      `Deploying script for Tenant ${themed(tenant, 'info')} Flow ${themed(flow, 'info')}${
        tap ? ` and Tap ${themed(tap, 'info')}` : ''
      } to ${themed(env, 'info')}`
    );
    !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
    // 1. build list of deployable files
    const deployableFiles = await getFolderFiles(folderPrefix, {
      recursive: true,
      filter: { pattern: '!((**/sync-output)|(**/etl-output)|(**/snapshots))' },
    });
    if (deployableFiles.length === 0) {
      json
        ? printJSON({ status: 'error', error: 'There are no files to deploy at the specified location!' })
        : spinner.fail(
            themed(`Error: ${themed('There are no files to deploy at the specified location!')}.`, 'secondary')
          );
      return;
    }

    // Validate files
    const filenames = deployableFiles.map((file) => path.basename(file));
    validateEtlScriptFiles(filenames);


    // 2. get STS credentials
    message = themed(`Verifying user and authorizing`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
    const params = {
      debug,
      baseUri: clientApiBaseUri,
      task: 'etl-deploy',
      env,
      tenant,
      flow,
      apikey,
    };
    if (tap) params.tap = tap;
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi(params);
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
    // create authenticated S3 instance
    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });

    const isV2 = await isV2Flow(s3, env, tenant, flow);

    // 3. verify location is valid (consider ListObjects policy + listObjectsV2 + scan folder for key = config.json)
    message = themed(`Validating flow and tap location`);
    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const supportedFlows = await getSupportedFlows({
      debug,
      baseUri: clientApiBaseUri,
      apikey,
      env,
    });

    const supportedFlow = supportedFlows.find(({ id }) => id === flow);

    let connectors = [];

    try {
      if (tenant === 'default' || supportedFlow?.type) {
        const supportedSources = isV2
          ? await getSupportedConnectors({
              debug,
              baseUri: clientApiBaseUri,
              env,
              flow,
              apikey,
            })
          : await getSupportedSources({
              debug,
              baseUri: clientApiBaseUri,
              env,
              flow,
              apikey,
            });
        connectors = (supportedSources ?? []).filter((ss) => (tap ? ss[isV2 ? 'id' : 'tap'] === tap : true));
        if (tap && connectors.length === 0) throw new Error('Tap is not supported');
      } else {
        const linkedSources = isV2
          ? await getLinkedConnectors({
              debug,
              baseUri: clientApiBaseUri,
              env,
              flow,
              tenant,
              apikey,
            })
          : await getLinkedSources({
              debug,
              baseUri: clientApiBaseUri,
              env,
              flow,
              tenant,
              apikey,
            });
        connectors = (linkedSources ?? []).filter((ls) => (tap ? ls[isV2 ? 'id' : 'tap'] === tap : true));
        if (tap && connectors.length === 0) throw new Error('Tap is not linked');
      }
    } catch (err) {
      debug('err', err);
      throw new Error(`Target location doesn't exist. Check your tenant, flow and tap arguments.`);
    }
    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    const table = new Table({ head: ['File', 'Status'] });
    for (const connector of connectors) {
      const connectorId = connector[isV2 ? 'id' : 'tap'];
      message = themed(`Preparing ${connectorId} deployment target`);
      !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
      const rootKey = `${tenant}/flows/${flow}/${await getEntityLabel(
        null,
        null,
        null,
        null,
        isV2
      )}/${connectorId}/etl/`;
      const { Contents } = await s3.listObjectsV2({ Bucket: env, Prefix: `${rootKey}` }).promise();
      !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
      debug('contents', Contents);
      // 4. cleanup old files
      const filesToDelete = Contents.map((item) => ({ Key: item.Key }));
      if (filesToDelete.length > 0) {
        message = themed(`Removing old ${connectorId} ETL files`);
        !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
        const delParams = {
          Bucket: env,
          Delete: { Objects: filesToDelete, Quiet: true },
        };
        await s3.deleteObjects(delParams).promise();
        filesToDelete.forEach(({ Key }) =>
          table.push([`${connectorId}/${Key.substring(rootKey.length)}`, themed('Deleted', 'warn')])
        );
        !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
      }
      // 5. upload new files
      // message = themed(`Deploying ETL scripts`);
      // !json && spinner.info(themed(`Info: ${message}.`, 'secondary'));
      for await (const file of deployableFiles) {
        const relativePath =
          process.platform === 'win32'
            ? path.relative(sourceFolder, file).replace(/\\/g, '/')
            : path.relative(sourceFolder, file);
        const key = `${rootKey}${relativePath}`;
        message = themed(`Deploying file: ${themed(`${connectorId}/${relativePath}`, 'info')}`);
        !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));
        const readBuffer = await readFile(file);
        const params = {
          Bucket: env,
          Key: key,
          Body: readBuffer,
        };
        const res = await s3.putObject(params).promise();
        !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));
        debug('s3-put-res', res);
        table.push([`${connectorId}/${relativePath}`, themed('Deployed', 'info')]);
      }
    }

    if (json) {
      printJSON({ status: 'success', deployedFiles: deployableFiles });
    } else {
      // print results
      cl(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
    debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
